import { AgentSkill, Skills } from '@nice-ccf/common-sdk';
import { Logger, HttpUtilService, HttpClient, HttpResponse,
         UrlUtilsService, ApiUriConstants, CXoneAuthBase, ValidationUtils } from '@nice-ccf/core-sdk';
import { SkillApiParser } from '../util/skill-api-parser';

/**
 * Class to perform get skills
 */
export class SkillService {
    protected logger: Logger = new Logger('SDK', 'SkillService');
    protected utilService: HttpUtilService = new HttpUtilService();
    private authBase: CXoneAuthBase;
    private apiParser: SkillApiParser = new SkillApiParser();
    private urlUtilsService: UrlUtilsService = new UrlUtilsService();
    private validationUtils = new ValidationUtils();
    /**
     * Create instance of CXoneAuthBase
     * ```
     * @example
     * const skillService = new SkillService();
     * ```
     */
    constructor() {
        this.authBase = CXoneAuthBase.Instance;
    }

    /**
     * Method to return agent skills
     * @param agentId - Pass the Agent Id
     * @returns - return the agent skills details
     * ```
     * @example
     * getAgentSkills('4712')
     * ```
     */
    public getAgentSkills(agentId?: string): Promise<AgentSkill[]> {
        const requiredAttributes = [
            'skillId', 'skillName', 'isOutbound', 'isDialer', 'isNaturalCalling',
            'outboundStrategy', 'priorityBlending', 'mediaTypeId', 'isRestricted',
            'isActive', 'useDisposition', 'useACW', 'useSecondaryDispositions',
            'requireDisposition', 'isNaturalCallingRunning'
        ];
        const requestParams = {
            fields: requiredAttributes.join(','),
            isSkillActive: true,
        };
        return new Promise<AgentSkill[]>((resolve, reject) => {
            const token = this.authBase.getAuthToken();
            const user = this.authBase.getUserDetails();
            const reqInit = this.utilService.initHeader(token.accessToken, 'application/json');
            const cxOneConfig = this.authBase.getCXoneConfig();
            agentId = agentId? agentId :user.icAgentId;
            let skillUrl = cxOneConfig.acdApiBaseUri + ApiUriConstants.GET_AGENT_SKILLS_URI.replace('{agentId}', agentId);
            skillUrl = this.urlUtilsService.appendQueryString(skillUrl, requestParams);
            if(this.validationUtils.isNotNullOrEmpty(agentId)){
            HttpClient.get(skillUrl, reqInit).then((resp: HttpResponse) => {
                this.logger.info('getAgentSkills', 'Get Agent skills Success');
                const skills = this.apiParser.parseAgentSkills(resp);
                resolve(skills);
            },
            (err: HttpResponse) => {
                this.logger.error('getAgentSkills', 'Get Agent skills Failed ' + JSON.stringify(err));
                reject(err);
            });
        } else{
            this.logger.error('getAgentSkills', 'agentId is empty');
            reject('agentId is empty');
        }
        });
    }

    /**
     * Method to return agent skills
     * @param mediaTypeId - Pass the media type Id
     * @param startIndex - Pass the start index
     * @param recordsToLoad - Pass the number of records to load
     * @param searchText - Pass the text string to search
     * @returns - return the agent skills details
     * ```
     * @example
     * getAllSkillsList(4,1,5,'call')
     * ```
     */
    public getAllSkillsList(mediaTypeId?: number, startIndex?: number, recordsToLoad?: number, searchText?: string): Promise<Skills[]> {
        const requiredAttributes = [
            'skillId', 'skillName','mediaTypeId', 'mediaTypeName','isActive','isOutbound',
        ];
        const requestParams={
            fields: requiredAttributes.join(','),
            isActive: true,
            orderby: "skillName",   
            searchString: searchText?searchText:"",
            ...((startIndex&&recordsToLoad) && { skip: startIndex, top:recordsToLoad }),
            ...(mediaTypeId&&{mediaTypeId:mediaTypeId})
        }
        
        return new Promise<Skills[]>((resolve, reject) => {
            const token = this.authBase.getAuthToken();
            const reqInit = this.utilService.initHeader(token.accessToken, 'application/json');
            const cxOneConfig = this.authBase.getCXoneConfig();
            let skillUrl = cxOneConfig.acdApiBaseUri + ApiUriConstants.GET_SKILLS_URI;
            skillUrl = this.urlUtilsService.appendQueryString(skillUrl, requestParams);
            HttpClient.get(skillUrl, reqInit).then((resp: HttpResponse) => {
                this.logger.info('getAllSkillsList', 'Get Agent skills Success');
                const skills = this.apiParser.parseAllSkillsList(resp);
                resolve(skills);
            },
            (err: HttpResponse) => {
                this.logger.error('getAllSkillsList', 'Get Agent skills Failed ' + JSON.stringify(err));
                reject(err);
            });
        });
    }
}
